// ============================================================
// DEVOPS, IAC & PRODUCTION ENGINEERING DATA
// Complete coverage of Cloud Platforms, CI/CD, IaC, SRE
// ============================================================

export const DEVOPS_ARCHITECTURE = {
  
  // ============================================================
  // INFRASTRUCTURE AS CODE (IaC)
  // ============================================================
  
  infrastructureAsCode: {
    category: 'Infrastructure as Code',
    layer: 'layer9',
    icon: '🏗️',
    components: [
      {
        id: 'iac-terraform',
        name: 'Terraform',
        purpose: 'Declarative infrastructure provisioning',
        function: 'Define cloud resources as code with state management',
        tags: ['iac', 'terraform', 'multicloud', 'aws', 'azure', 'gcp'],
        cost: { low: 0, high: 500, unit: 'per month (Cloud)' },
        applications: [
          'Multi-cloud infrastructure provisioning',
          'VPC, networking, security groups',
          'EKS, RDS, S3, Lambda provisioning',
          'Environment replication (dev/staging/prod)'
        ],
        bestPractices: [
          'Remote state in S3 + DynamoDB locking',
          'Modular structure (modules per service)',
          'Workspaces for environments',
          'Policy as Code with Sentinel/OPA'
        ],
        alternatives: ['Pulumi', 'AWS CDK', 'CloudFormation', 'Crossplane']
      },
      {
        id: 'iac-pulumi',
        name: 'Pulumi',
        purpose: 'IaC using programming languages',
        function: 'Define infrastructure using TypeScript/Python/Go',
        tags: ['iac', 'typescript', 'python', 'multicloud'],
        cost: { low: 0, high: 1000, unit: 'per month' },
        applications: [
          'Complex infrastructure with logic',
          'Type-safe infrastructure',
          'Reusable components in code'
        ],
        bestPractices: [
          'Use Pulumi ESC for secrets',
          'ComponentResource for reusability',
          'Stack references for cross-stack'
        ],
        alternatives: ['Terraform', 'AWS CDK', 'CloudFormation']
      },
      {
        id: 'iac-cdk',
        name: 'AWS CDK',
        purpose: 'AWS infrastructure in code',
        function: 'Define AWS resources with TypeScript/Python',
        tags: ['iac', 'aws', 'typescript', 'python'],
        cost: { low: 0, high: 0, unit: 'free' },
        applications: [
          'AWS-native IaC',
          'Type-safe AWS resources',
          'Reusable constructs'
        ],
        bestPractices: [
          'Use L2/L3 constructs',
          'CDK Pipelines for CI/CD',
          'Aspects for cross-cutting concerns'
        ],
        alternatives: ['Terraform', 'Pulumi', 'CloudFormation']
      },
      {
        id: 'iac-ansible',
        name: 'Ansible',
        purpose: 'Configuration management',
        function: 'Automate server configuration and deployment',
        tags: ['config-management', 'automation', 'serverless'],
        cost: { low: 0, high: 2000, unit: 'per month (AAP)' },
        applications: [
          'Server configuration',
          'Application deployment',
          'Configuration drift remediation'
        ],
        alternatives: ['Chef', 'Puppet', 'SaltStack']
      }
    ]
  },
  
  // ============================================================
  // CI/CD PIPELINES
  // ============================================================
  
  cicdPipelines: {
    category: 'CI/CD Pipelines',
    layer: 'layer9',
    icon: '🔄',
    components: [
      {
        id: 'cicd-github-actions',
        name: 'GitHub Actions',
        purpose: 'CI/CD automation',
        function: 'Build, test, deploy on Git events',
        tags: ['ci-cd', 'automation', 'github'],
        cost: { low: 0, high: 200, unit: 'per month' },
        applications: [
          'Automated testing on PR',
          'Container builds and pushes',
          'Multi-environment deployment'
        ],
        bestPractices: [
          'Reusable workflows',
          'OIDC for cloud authentication (no static keys)',
          'Matrix builds for multi-version testing',
          'Environments with required reviewers'
        ],
        alternatives: ['GitLab CI', 'Jenkins', 'CircleCI', 'Azure DevOps']
      },
      {
        id: 'cicd-gitlab-ci',
        name: 'GitLab CI/CD',
        purpose: 'Integrated DevOps platform',
        function: 'Complete DevOps with SCM integration',
        tags: ['ci-cd', 'devops', 'gitlab'],
        cost: { low: 0, high: 500, unit: 'per month' },
        applications: [
          'End-to-end DevOps',
          'Security scanning integrated',
          'Kubernetes deployment'
        ],
        alternatives: ['GitHub Actions', 'Jenkins', 'Azure DevOps']
      },
      {
        id: 'cicd-argocd',
        name: 'ArgoCD',
        purpose: 'GitOps deployment',
        function: 'Declarative continuous delivery to Kubernetes',
        tags: ['gitops', 'kubernetes', 'cd', 'argocd'],
        cost: { low: 0, high: 0, unit: 'free' },
        applications: [
          'GitOps workflows',
          'Kubernetes deployments',
          'Multi-cluster management'
        ],
        bestPractices: [
          'App-of-apps pattern',
          'Sync waves for ordering',
          'Progressive delivery with Argo Rollouts'
        ],
        alternatives: ['Flux', 'Jenkins X', 'Spinnaker']
      },
      {
        id: 'cicd-argo-rollouts',
        name: 'Argo Rollouts',
        purpose: 'Progressive delivery',
        function: 'Canary and blue-green deployments',
        tags: ['progressive-delivery', 'canary', 'blue-green'],
        cost: { low: 0, high: 0, unit: 'free' },
        applications: [
          'Canary releases',
          'Blue-green deployments',
          'Automated rollbacks'
        ],
        alternatives: ['Flagger', 'Spinnaker']
      }
    ]
  },
  
  // ============================================================
  // CONTAINER & ORCHESTRATION
  // ============================================================
  
  containerOrchestration: {
    category: 'Container Orchestration',
    layer: 'layer9',
    icon: '☸️',
    components: [
      {
        id: 'container-eks',
        name: 'AWS EKS',
        purpose: 'Managed Kubernetes',
        function: 'AWS-managed Kubernetes control plane',
        tags: ['kubernetes', 'aws', 'managed', 'eks'],
        cost: { low: 73, high: 10000, unit: 'per month' },
        applications: [
          'Production AI workloads',
          'ML model serving',
          'Microservices deployment'
        ],
        bestPractices: [
          'Use managed node groups',
          'Karpenter for autoscaling',
          'IRSA for pod-level IAM',
          'Spot instances for non-critical'
        ],
        alternatives: ['GKE', 'AKS', 'Self-managed K8s', 'ECS']
      },
      {
        id: 'container-karpenter',
        name: 'Karpenter',
        purpose: 'Kubernetes autoscaling',
        function: 'Just-in-time node provisioning',
        tags: ['autoscaling', 'kubernetes', 'efficiency'],
        cost: { low: 0, high: 0, unit: 'free' },
        applications: [
          'Cost optimization',
          'Fast scaling',
          'Heterogeneous workloads'
        ],
        alternatives: ['Cluster Autoscaler', 'GKE Autopilot']
      },
      {
        id: 'container-istio',
        name: 'Istio Service Mesh',
        purpose: 'Service-to-service communication',
        function: 'Traffic management, security, observability',
        tags: ['service-mesh', 'mtls', 'observability'],
        cost: { low: 0, high: 5000, unit: 'per month' },
        applications: [
          'mTLS between services',
          'Traffic routing',
          'Observability across services'
        ],
        alternatives: ['Linkerd', 'Consul Connect', 'Cilium Service Mesh']
      }
    ]
  },
  
  // ============================================================
  // OBSERVABILITY & SRE
  // ============================================================
  
  observabilityAndSRE: {
    category: 'Observability & SRE',
    layer: 'layer11',
    icon: '📊',
    components: [
      {
        id: 'obs-opentelemetry',
        name: 'OpenTelemetry',
        purpose: 'Unified observability',
        function: 'Collect traces, metrics, and logs',
        tags: ['observability', 'traces', 'metrics', 'logs'],
        cost: { low: 0, high: 0, unit: 'free (collector)' },
        applications: [
          'Distributed tracing',
          'Unified telemetry pipeline',
          'Vendor-neutral instrumentation'
        ],
        bestPractices: [
          'Auto-instrumentation first',
          'Sampling strategies for cost',
          'Semantic conventions'
        ],
        alternatives: ['Jaeger', 'Zipkin', 'Datadog APM']
      },
      {
        id: 'obs-datadog',
        name: 'Datadog',
        purpose: 'Full-stack observability',
        function: 'APM, logs, metrics, RUM, synthetics',
        tags: ['apm', 'monitoring', 'saas', 'observability'],
        cost: { low: 15, high: 10000, unit: 'per host/month' },
        applications: [
          'Production monitoring',
          'Incident management',
          'AI model monitoring'
        ],
        alternatives: ['New Relic', 'Dynatrace', 'Grafana Cloud']
      },
      {
        id: 'obs-grafana-stack',
        name: 'Grafana Stack (LGTM)',
        purpose: 'Open-source observability',
        function: 'Loki (logs), Grafana, Tempo (traces), Mimir (metrics)',
        tags: ['observability', 'open-source', 'grafana'],
        cost: { low: 0, high: 5000, unit: 'per month' },
        applications: [
          'Self-hosted observability',
          'Cost-effective at scale',
          'Unified dashboards'
        ],
        alternatives: ['ELK Stack', 'Datadog', 'New Relic']
      },
      {
        id: 'obs-slo-sre',
        name: 'SLO/SLI Framework',
        purpose: 'Service level objectives',
        function: 'Define error budgets and reliability targets',
        tags: ['sre', 'slo', 'sli', 'error-budget'],
        cost: { low: 0, high: 500, unit: 'per month' },
        applications: [
          'Reliability engineering',
          'Error budget tracking',
          'Incident response prioritization'
        ],
        bestPractices: [
          'Define SLIs from user perspective',
          'Set SLOs with stakeholders',
          'Track error budgets'
        ],
        alternatives: ['Nobl9', 'Datadog SLO', 'Custom implementation']
      },
      {
        id: 'obs-chaos-engineering',
        name: 'Chaos Engineering',
        purpose: 'Resilience testing',
        function: 'Inject failures to test system resilience',
        tags: ['chaos', 'resilience', 'testing', 'sre'],
        cost: { low: 0, high: 2000, unit: 'per month' },
        applications: [
          'Test failure scenarios',
          'Validate recovery procedures',
          'Build confidence in system'
        ],
        tools: ['AWS FIS', 'Chaos Monkey', 'Litmus', 'Gremlin'],
        alternatives: ['Manual testing', 'Game days']
      }
    ]
  },
  
  // ============================================================
  // DURABILITY & HIGH AVAILABILITY
  // ============================================================
  
  durabilityAndHA: {
    category: 'Durability & High Availability',
    layer: 'layer9',
    icon: '🛡️',
    components: [
      {
        id: 'ha-multi-region',
        name: 'Multi-Region Deployment',
        purpose: 'Geographic redundancy',
        function: 'Deploy across multiple AWS regions',
        tags: ['ha', 'multi-region', 'disaster-recovery'],
        cost: { low: 1000, high: 100000, unit: 'per month' },
        applications: [
          'Disaster recovery',
          'Global low latency',
          'Regulatory data residency'
        ],
        patterns: [
          'Active-Active (both regions serve traffic)',
          'Active-Passive (one primary, one standby)',
          'Pilot Light (minimal standby)',
          'Warm Standby (scaled-down replica)'
        ],
        alternatives: ['Multi-AZ', 'Single region + backup']
      },
      {
        id: 'ha-multi-az',
        name: 'Multi-AZ Deployment',
        purpose: 'Availability zone redundancy',
        function: 'Deploy across multiple AZs in a region',
        tags: ['ha', 'multi-az', 'availability'],
        cost: { low: 100, high: 10000, unit: 'per month' },
        applications: [
          'Standard HA',
          'Database replication',
          'Load-balanced services'
        ],
        alternatives: ['Multi-region', 'Single AZ']
      },
      {
        id: 'ha-backup-dr',
        name: 'Backup & Disaster Recovery',
        purpose: 'Data protection',
        function: 'Automated backups and recovery procedures',
        tags: ['backup', 'dr', 'recovery'],
        cost: { low: 50, high: 5000, unit: 'per month' },
        applications: [
          'Data loss prevention',
          'Ransomware recovery',
          'Compliance retention'
        ],
        metrics: {
          RPO: 'Recovery Point Objective - how much data loss acceptable',
          RTO: 'Recovery Time Objective - how much downtime acceptable'
        },
        alternatives: ['AWS Backup', 'Veeam', 'Custom scripts']
      },
      {
        id: 'ha-circuit-breaker',
        name: 'Circuit Breaker Pattern',
        purpose: 'Fault tolerance',
        function: 'Prevent cascading failures',
        tags: ['resilience', 'fault-tolerance', 'patterns'],
        cost: { low: 0, high: 0, unit: 'free (library)' },
        applications: [
          'Microservices resilience',
          'External API protection',
          'Database connection management'
        ],
        alternatives: ['Retry with backoff', 'Bulkhead', 'Timeout']
      }
    ]
  },
  
  // ============================================================
  // SECRETS & SECURITY
  // ============================================================
  
  secretsAndSecurity: {
    category: 'Secrets & Security',
    layer: 'layer12',
    icon: '🔐',
    components: [
      {
        id: 'sec-vault',
        name: 'HashiCorp Vault',
        purpose: 'Secrets management',
        function: 'Centralized secrets with dynamic credentials',
        tags: ['secrets', 'security', 'vault'],
        cost: { low: 0, high: 5000, unit: 'per month' },
        applications: [
          'Database credentials',
          'API keys',
          'Certificate management'
        ],
        bestPractices: [
          'Dynamic secrets for databases',
          'Audit logging enabled',
          'Namespaces for multi-tenancy'
        ],
        alternatives: ['AWS Secrets Manager', 'Azure Key Vault', 'Doppler']
      },
      {
        id: 'sec-aws-secrets',
        name: 'AWS Secrets Manager',
        purpose: 'AWS-native secrets',
        function: 'Manage secrets with rotation',
        tags: ['secrets', 'aws', 'managed'],
        cost: { low: 0.40, high: 500, unit: 'per secret/month' },
        applications: [
          'RDS credentials',
          'Third-party API keys',
          'Automatic rotation'
        ],
        alternatives: ['HashiCorp Vault', 'AWS Parameter Store']
      },
      {
        id: 'sec-oidc',
        name: 'OIDC Federation',
        purpose: 'Passwordless authentication',
        function: 'Federate identity between systems',
        tags: ['authentication', 'oidc', 'zero-trust'],
        cost: { low: 0, high: 0, unit: 'free' },
        applications: [
          'CI/CD to cloud (GitHub → AWS)',
          'Kubernetes pod identity',
          'Service-to-service auth'
        ],
        advantages: ['No static credentials', 'Short-lived tokens', 'Auditable'],
        alternatives: ['IAM roles', 'Service accounts', 'API keys']
      }
    ]
  },
  
  // ============================================================
  // EVENT-DRIVEN ARCHITECTURE
  // ============================================================
  
  eventDrivenArchitecture: {
    category: 'Event-Driven Architecture',
    layer: 'layer9',
    icon: '📡',
    components: [
      {
        id: 'event-eventbridge',
        name: 'AWS EventBridge',
        purpose: 'Serverless event bus',
        function: 'Route events between AWS services and SaaS',
        tags: ['events', 'serverless', 'aws', 'event-bus'],
        cost: { low: 0, high: 1000, unit: 'per month' },
        applications: [
          'Microservice communication',
          'SaaS integration',
          'Event-driven workflows'
        ],
        bestPractices: [
          'Event schema registry',
          'Dead letter queues',
          'Event replay capability'
        ],
        alternatives: ['Kafka', 'RabbitMQ', 'Google Pub/Sub']
      },
      {
        id: 'event-sqs-sns',
        name: 'SQS + SNS',
        purpose: 'Queue and pub/sub',
        function: 'Decouple services with queues and topics',
        tags: ['queue', 'pubsub', 'aws', 'messaging'],
        cost: { low: 0, high: 500, unit: 'per month' },
        applications: [
          'Async processing',
          'Fan-out notifications',
          'Dead letter queues'
        ],
        alternatives: ['Kafka', 'RabbitMQ', 'Redis Streams']
      },
      {
        id: 'event-kafka',
        name: 'Apache Kafka (MSK)',
        purpose: 'Event streaming platform',
        function: 'High-throughput event streaming',
        tags: ['kafka', 'streaming', 'events', 'msk'],
        cost: { low: 100, high: 10000, unit: 'per month' },
        applications: [
          'Real-time data pipelines',
          'Event sourcing',
          'Stream processing'
        ],
        bestPractices: [
          'Topic design for partition strategy',
          'Schema registry for compatibility',
          'Consumer groups for scaling'
        ],
        alternatives: ['AWS Kinesis', 'Pulsar', 'Redpanda']
      }
    ]
  },
  
  // ============================================================
  // SERVERLESS COMPUTING
  // ============================================================
  
  serverlessComputing: {
    category: 'Serverless Computing',
    layer: 'layer8',
    icon: '⚡',
    components: [
      {
        id: 'serverless-lambda',
        name: 'AWS Lambda',
        purpose: 'Serverless functions',
        function: 'Run code without managing servers',
        tags: ['serverless', 'aws', 'lambda', 'functions'],
        cost: { low: 0, high: 1000, unit: 'per month' },
        applications: [
          'API backends',
          'Event processing',
          'Scheduled tasks'
        ],
        bestPractices: [
          'Use layers for dependencies',
          'Provisioned concurrency for latency',
          'Reserved concurrency for protection'
        ],
        alternatives: ['Cloud Functions', 'Azure Functions', 'Cloudflare Workers']
      },
      {
        id: 'serverless-fargate',
        name: 'AWS Fargate',
        purpose: 'Serverless containers',
        function: 'Run containers without managing EC2',
        tags: ['serverless', 'containers', 'fargate'],
        cost: { low: 0, high: 5000, unit: 'per month' },
        applications: [
          'Long-running services',
          'Batch jobs',
          'Web applications'
        ],
        alternatives: ['EKS with Karpenter', 'Cloud Run', 'App Runner']
      },
      {
        id: 'serverless-sagemaker',
        name: 'AWS SageMaker Serverless',
        purpose: 'Serverless ML inference',
        function: 'Auto-scaling ML endpoints',
        tags: ['serverless', 'ml', 'sagemaker'],
        cost: { low: 0, high: 5000, unit: 'per month' },
        applications: [
          'Variable-traffic inference',
          'Cost-optimized ML',
          'Intermittent workloads'
        ],
        alternatives: ['Lambda with ML', 'EKS with KServe', 'Bedrock']
      }
    ]
  }
};

// ============================================================
// PROGRAMMING & ENGINEERING STANDARDS
// ============================================================

export const ENGINEERING_STANDARDS = {
  
  programmingLanguages: {
    category: 'Programming Languages',
    icon: '💻',
    languages: [
      {
        id: 'lang-python',
        name: 'Python',
        proficiency: 'Advanced',
        useCases: [
          'Backend development (FastAPI, Django, Flask)',
          'ML/AI development (PyTorch, TensorFlow, LangChain)',
          'Data engineering (Pandas, PySpark, Airflow)',
          'Scripting and automation'
        ],
        bestPractices: [
          'Type hints with mypy/pyright',
          'Poetry or uv for dependency management',
          'Black + Ruff for formatting/linting',
          'Pytest for testing',
          'Pydantic for data validation'
        ],
        frameworks: {
          web: ['FastAPI', 'Django', 'Flask', 'Litestar'],
          ml: ['PyTorch', 'TensorFlow', 'JAX', 'scikit-learn'],
          llm: ['LangChain', 'LlamaIndex', 'Haystack', 'CrewAI'],
          data: ['Pandas', 'Polars', 'PySpark', 'Dask']
        }
      },
      {
        id: 'lang-typescript',
        name: 'TypeScript',
        proficiency: 'Strong',
        useCases: [
          'Frontend development (React, Vue, Next.js)',
          'Backend with Node.js',
          'IaC with CDK/Pulumi',
          'Full-stack applications'
        ],
        bestPractices: [
          'Strict TypeScript configuration',
          'ESLint + Prettier',
          'Zod for runtime validation',
          'Vitest/Jest for testing'
        ],
        frameworks: {
          frontend: ['React', 'Vue', 'Next.js', 'SvelteKit'],
          backend: ['NestJS', 'Express', 'Fastify', 'Hono'],
          iac: ['AWS CDK', 'Pulumi']
        }
      },
      {
        id: 'lang-go',
        name: 'Go',
        proficiency: 'Intermediate',
        useCases: [
          'High-performance microservices',
          'CLI tools',
          'Cloud-native infrastructure',
          'Kubernetes operators'
        ],
        bestPractices: [
          'Standard project layout',
          'Context propagation',
          'Error wrapping',
          'Table-driven tests'
        ]
      }
    ]
  },
  
  ciCdPractices: {
    category: 'CI/CD Best Practices',
    practices: [
      {
        name: 'Trunk-Based Development',
        description: 'Short-lived branches merged to main frequently',
        tools: ['GitHub Flow', 'GitLab Flow']
      },
      {
        name: 'Automated Testing Pyramid',
        description: '70% unit, 20% integration, 10% E2E',
        tools: ['pytest', 'Jest', 'Playwright', 'Testcontainers']
      },
      {
        name: 'Container Security Scanning',
        description: 'Scan images for vulnerabilities before deployment',
        tools: ['Trivy', 'Snyk', 'Grype', 'AWS ECR Scanning']
      },
      {
        name: 'SAST & DAST',
        description: 'Static and dynamic application security testing',
        tools: ['SonarQube', 'Semgrep', 'OWASP ZAP', 'CodeQL']
      },
      {
        name: 'Artifact Management',
        description: 'Version and store all build artifacts',
        tools: ['Artifactory', 'Nexus', 'AWS ECR', 'GitHub Packages']
      },
      {
        name: 'Environment Parity',
        description: 'Dev, staging, prod should be as similar as possible',
        tools: ['Terraform', 'Docker', 'Kubernetes']
      },
      {
        name: 'Feature Flags',
        description: 'Deploy code without exposing to users',
        tools: ['LaunchDarkly', 'Unleash', 'Flagsmith', 'AWS AppConfig']
      },
      {
        name: 'Progressive Delivery',
        description: 'Canary, blue-green, ring deployments',
        tools: ['Argo Rollouts', 'Flagger', 'Spinnaker']
      }
    ]
  },
  
  codeQualityStandards: {
    category: 'Code Quality Standards',
    standards: [
      {
        name: 'SonarQube Quality Gate',
        metrics: [
          'Coverage > 80%',
          'Duplications < 3%',
          'Technical Debt Ratio < 5%',
          'No new critical/blocker issues'
        ]
      },
      {
        name: 'Peer Review Standards',
        metrics: [
          'At least 2 approvals for production code',
          'All comments addressed',
          'Tests included',
          'Documentation updated'
        ]
      },
      {
        name: 'Code Review for AI-Generated Code',
        metrics: [
          'Human verification required',
          'Security review mandatory',
          'License check for generated code',
          'Originality verification'
        ]
      }
    ]
  }
};

// ============================================================
// STAKEHOLDER COMMUNICATION & GOVERNANCE
// ============================================================

export const GOVERNANCE_ARTIFACTS = {
  
  architectureDecisionRecords: {
    category: 'Architecture Decision Records (ADRs)',
    icon: '📝',
    templates: [
      {
        id: 'adr-template',
        title: 'ADR Template',
        structure: {
          title: 'ADR-XXX: Decision Title',
          status: 'Proposed | Accepted | Rejected | Superseded',
          context: 'What is the issue we are facing?',
          decision: 'What is the change we are proposing?',
          consequences: 'What becomes easier or harder?',
          alternatives: 'What other options were considered?',
          stakeholders: 'Who is affected by this decision?',
          date: 'YYYY-MM-DD'
        }
      }
    ],
    examples: [
      'ADR-001: Use LangChain for RAG orchestration',
      'ADR-002: Deploy on AWS EKS instead of ECS',
      'ADR-003: Use Pinecone for vector storage',
      'ADR-004: Adopt OpenTelemetry for observability'
    ]
  },
  
  architectureReviewProcess: {
    category: 'Architecture Review Process',
    icon: '🎯',
    steps: [
      {
        step: 1,
        name: 'Pre-Review Submission',
        artifacts: ['Design doc', 'ADRs', 'Diagrams', 'Security assessment'],
        duration: '1 week before review'
      },
      {
        step: 2,
        name: 'Review Meeting',
        participants: ['AI Architect', 'Security', 'SRE', 'Product Owner', 'Business Stakeholder'],
        duration: '60-90 minutes'
      },
      {
        step: 3,
        name: 'Action Items',
        artifacts: ['Review notes', 'Action items', 'Approval status'],
        duration: '3 days after review'
      },
      {
        step: 4,
        name: 'Sign-off',
        artifacts: ['Final approval', 'Updated ADRs', 'Communication plan'],
        duration: '1 week'
      }
    ]
  },
  
  designDocumentTemplate: {
    category: 'Design Document Template',
    icon: '📄',
    sections: [
      'Executive Summary',
      'Problem Statement',
      'Business Context',
      'Proposed Solution',
      'Architecture Diagrams',
      'Component Details',
      'Data Flow',
      'Security Considerations',
      'Performance & Scalability',
      'Observability Strategy',
      'Cost Analysis',
      'Risk Assessment',
      'Implementation Roadmap',
      'Alternatives Considered',
      'Open Questions',
      'Appendix'
    ]
  },
  
  stakeholderCommunication: {
    category: 'Stakeholder Communication',
    icon: '💬',
    strategies: [
      {
        audience: 'Non-Technical Senior Stakeholders',
        approach: 'Business value focus',
        artifacts: ['Executive summaries', 'Visual diagrams', 'Cost-benefit analysis', 'Timeline roadmaps']
      },
      {
        audience: 'Technical Teams',
        approach: 'Technical depth',
        artifacts: ['ADRs', 'Sequence diagrams', 'API specs', 'Runbooks']
      },
      {
        audience: 'Product Owners',
        approach: 'Feature-focused',
        artifacts: ['User stories', 'Acceptance criteria', 'Demo plans']
      },
      {
        audience: 'Security & Compliance',
        approach: 'Risk-focused',
        artifacts: ['Threat models', 'Security assessments', 'Compliance mappings']
      }
    ]
  }
};

// ============================================================
// REGULATORY COMPLIANCE (Highly Regulated Industries)
// ============================================================

export const REGULATORY_COMPLIANCE = {
  
  healthcareLifeSciences: {
    category: 'Healthcare & Life Sciences',
    regulations: [
      {
        id: 'reg-gxp',
        name: 'GxP (Good Practice)',
        scope: 'Pharmaceutical and medical device',
        requirements: [
          'Audit trails for all data changes',
          'Electronic signatures (21 CFR Part 11)',
          'Validation of computer systems (CSV)',
          'Data integrity (ALCOA+ principles)'
        ],
        aiImplications: [
          'Model validation and documentation',
          'Change control for model updates',
          'Data provenance tracking',
          'Human-in-the-loop for critical decisions'
        ]
      },
      {
        id: 'reg-fda',
        name: 'FDA AI/ML Guidance',
        scope: 'AI/ML in medical devices (SaMD)',
        requirements: [
          'Predetermined change control plan (PCCP)',
          'Transparency documentation',
          'Real-world performance monitoring'
        ],
        aiImplications: [
          'Continuous learning model oversight',
          'Bias monitoring across populations',
          'Adverse event reporting'
        ]
      },
      {
        id: 'reg-hipaa',
        name: 'HIPAA',
        scope: 'Protected Health Information (PHI)',
        requirements: [
          'Administrative, physical, technical safeguards',
          'Business Associate Agreements (BAAs)',
          'Breach notification requirements'
        ],
        aiImplications: [
          'PHI de-identification for training',
          'Access controls on model endpoints',
          'Audit logging of PHI access',
          'Encryption at rest and in transit'
        ]
      },
      {
        id: 'reg-gdpr',
        name: 'GDPR',
        scope: 'EU personal data',
        requirements: [
          'Right to explanation for automated decisions',
          'Right to erasure (right to be forgotten)',
          'Data portability',
          'Consent management'
        ],
        aiImplications: [
          'Explainable AI (XAI) mandatory',
          'Model retraining for data deletion',
          'Consent tracking in training data',
          'Cross-border data transfer compliance'
        ]
      }
    ]
  },
  
  financialServices: {
    category: 'Financial Services',
    regulations: [
      {
        id: 'reg-soc2',
        name: 'SOC 2 Type II',
        scope: 'Service organizations',
        requirements: [
          'Security, availability, processing integrity',
          'Confidentiality, privacy controls',
          'Annual audit and continuous monitoring'
        ]
      },
      {
        id: 'reg-pci',
        name: 'PCI DSS',
        scope: 'Payment card data',
        requirements: [
          'Encryption of cardholder data',
          'Access control measures',
          'Regular security testing'
        ]
      },
      {
        id: 'reg-basel',
        name: 'Basel III/IV',
        scope: 'Banking risk management',
        requirements: [
          'Model risk management (SR 11-7)',
          'Stress testing',
          'Capital adequacy calculations'
        ],
        aiImplications: [
          'Model validation documentation',
          'Model performance monitoring',
          'Model inventory and governance'
        ]
      }
    ]
  },
  
  aiGovernance: {
    category: 'AI Governance',
    frameworks: [
      {
        id: 'gov-nist-ai',
        name: 'NIST AI RMF',
        description: 'NIST AI Risk Management Framework',
        functions: ['Govern', 'Map', 'Measure', 'Manage'],
        applicability: 'US federal and general best practice'
      },
      {
        id: 'gov-eu-ai-act',
        name: 'EU AI Act',
        description: 'Risk-based AI regulation',
        riskLevels: [
          { level: 'Unacceptable', examples: ['Social scoring', 'Subliminal manipulation'] },
          { level: 'High', examples: ['Critical infrastructure', 'Education', 'Employment'] },
          { level: 'Limited', examples: ['Chatbots', 'Emotion recognition'] },
          { level: 'Minimal', examples: ['Spam filters', 'AI in games'] }
        ]
      },
      {
        id: 'gov-iso-42001',
        name: 'ISO/IEC 42001',
        description: 'AI Management System',
        requirements: [
          'AI policy and objectives',
          'Risk assessment process',
          'AI system lifecycle management',
          'Continuous improvement'
        ]
      }
    ]
  }
};

// ============================================================
// RDT (Roche Digital Technology) CONTEXT FITTING
// ============================================================

export const RDT_CONTEXT = {
  
  architecturalPatterns: {
    category: 'RDT Architectural Patterns',
    patterns: [
      {
        name: 'API-First Design',
        description: 'All services expose well-designed APIs',
        requirements: ['OpenAPI 3.0 specs', 'Versioning strategy', 'Backward compatibility']
      },
      {
        name: 'Event-Driven Integration',
        description: 'Loose coupling via events',
        requirements: ['Schema registry', 'Event versioning', 'Idempotency']
      },
      {
        name: 'Domain-Driven Design',
        description: 'Bounded contexts aligned to business domains',
        requirements: ['Context mapping', 'Ubiquitous language', 'Aggregates']
      },
      {
        name: 'Zero-Trust Security',
        description: 'Never trust, always verify',
        requirements: ['mTLS everywhere', 'Short-lived credentials', 'Least privilege']
      },
      {
        name: 'Data Mesh',
        description: 'Decentralized data ownership',
        requirements: ['Data products', 'Federated governance', 'Self-serve platform']
      }
    ]
  },
  
  businessFunctions: {
    category: 'RDT Business Functions',
    functions: [
      'Diagnostics (Roche Diagnostics)',
      'Pharmaceuticals (Roche Pharma)',
      'Personalized Healthcare',
      'Digital Pathology',
      'Clinical Trials',
      'Manufacturing & Supply Chain',
      'Commercial Operations',
      'Finance & Compliance'
    ]
  },
  
  aiStandards: {
    category: 'AI Standards at RDT',
    standards: [
      'Responsible AI Principles',
      'Data Privacy by Design',
      'Model Governance (Model Cards)',
      'AI Ethics Review Board',
      'Explainability Requirements',
      'Bias Testing Standards',
      'Human-in-the-Loop Guidelines'
    ]
  }
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

export function getAllDevOpsComponents() {
  const components = [];
  Object.values(DEVOPS_ARCHITECTURE).forEach(category => {
    if (category.components) {
      category.components.forEach(comp => {
        components.push({
          ...comp,
          category: category.category,
          categoryIcon: category.icon
        });
      });
    }
  });
  return components;
}

export function getAllGovernanceArtifacts() {
  const artifacts = [];
  Object.values(GOVERNANCE_ARTIFACTS).forEach(category => {
    artifacts.push({
      category: category.category,
      icon: category.icon,
      items: category.templates || category.steps || category.sections || category.strategies || []
    });
  });
  return artifacts;
}

export function getAllComplianceRequirements() {
  const requirements = [];
  Object.values(REGULATORY_COMPLIANCE).forEach(domain => {
    if (domain.regulations) {
      domain.regulations.forEach(reg => {
        requirements.push({
          domain: domain.category,
          ...reg
        });
      });
    }
  });
  return requirements;
}

export default DEVOPS_ARCHITECTURE;